import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Trophy, 
  Users, 
  Building2, 
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Minus,
  Download,
  FileSpreadsheet,
  FileText
} from "lucide-react";
import { CurrencyDisplay } from "./CurrencySettings";
import * as XLSX from 'xlsx';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

// Progress Ring Component
const ProgressRing = ({ percentage, size = 120, strokeWidth = 10, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(percentage, 100) / 100) * circumference;
  
  // Determine color based on percentage
  const ringColor = color || (
    percentage >= 100 ? "#22c55e" : 
    percentage >= 80 ? "#eab308" : 
    percentage >= 50 ? "#f97316" : 
    "#ef4444"
  );
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: ringColor }}>
          {percentage.toFixed(1)}%
        </span>
        <span className="text-xs text-gray-500">Achievement</span>
      </div>
    </div>
  );
};

// Rank Badge Component
const RankBadge = ({ rank }) => {
  const getBadgeStyle = () => {
    if (rank === 1) return "bg-yellow-100 text-yellow-800 border-yellow-300";
    if (rank === 2) return "bg-gray-100 text-gray-700 border-gray-300";
    if (rank === 3) return "bg-orange-100 text-orange-800 border-orange-300";
    return "bg-slate-50 text-slate-600 border-slate-200";
  };
  
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-sm ${getBadgeStyle()}`}>
      {rank}
    </span>
  );
};

// Variance Indicator
const VarianceIndicator = ({ variance }) => {
  if (variance > 0) {
    return (
      <span className="inline-flex items-center text-green-600 text-sm font-medium">
        <ChevronUp className="w-4 h-4" />
        <CurrencyDisplay amount={variance} />
      </span>
    );
  } else if (variance < 0) {
    return (
      <span className="inline-flex items-center text-red-600 text-sm font-medium">
        <ChevronDown className="w-4 h-4" />
        <CurrencyDisplay amount={Math.abs(variance)} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-gray-500 text-sm">
      <Minus className="w-4 h-4" /> On Target
    </span>
  );
};

const TargetAnalytics = ({ restaurants, user }) => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const currentFiscalYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [brands, setBrands] = useState([]);
  const [storeManagers, setStoreManagers] = useState([]);
  
  // Filters
  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear.toString());
  const [restaurantId, setRestaurantId] = useState("all");
  const [brandId, setBrandId] = useState("all");
  const [periodType, setPeriodType] = useState("all");
  const [periodValue, setPeriodValue] = useState("all");
  const [storeManagerId, setStoreManagerId] = useState("all");

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get("/brands");
      setBrands(response.data);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
    }
  };

  const fetchStoreManagers = async () => {
    try {
      const response = await axiosInstance.get("/store-managers");
      setStoreManagers(response.data);
    } catch (error) {
      console.error("Failed to fetch store managers:", error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fiscalYear && fiscalYear !== "all") params.append("fiscal_year", fiscalYear);
      if (restaurantId && restaurantId !== "all") params.append("restaurant_id", restaurantId);
      if (brandId && brandId !== "all") params.append("brand_id", brandId);
      if (periodType && periodType !== "all") params.append("period_type", periodType);
      if (periodValue && periodValue !== "all" && periodType !== "all") params.append("period_value", periodValue);
      if (storeManagerId && storeManagerId !== "all") params.append("store_manager_id", storeManagerId);
      
      const response = await axiosInstance.get(`/target-analytics?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
    fetchStoreManagers();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fiscalYear, restaurantId, brandId, periodType, periodValue, storeManagerId]);

  const generateFiscalYears = () => {
    const years = [];
    for (let i = currentFiscalYear; i >= currentFiscalYear - 5; i--) {
      years.push({ value: i.toString(), label: `FY ${i}-${(i + 1).toString().slice(2)}` });
    }
    return years;
  };

  const FISCAL_MONTHS = [
    { value: "1", label: "April", short: "Apr" }, { value: "2", label: "May", short: "May" }, { value: "3", label: "June", short: "Jun" },
    { value: "4", label: "July", short: "Jul" }, { value: "5", label: "August", short: "Aug" }, { value: "6", label: "September", short: "Sep" },
    { value: "7", label: "October", short: "Oct" }, { value: "8", label: "November", short: "Nov" }, { value: "9", label: "December", short: "Dec" },
    { value: "10", label: "January", short: "Jan" }, { value: "11", label: "February", short: "Feb" }, { value: "12", label: "March", short: "Mar" },
  ];
  const QUARTERS = [
    { value: "1", label: "Q1 (Apr-Jun)" }, { value: "2", label: "Q2 (Jul-Sep)" },
    { value: "3", label: "Q3 (Oct-Dec)" }, { value: "4", label: "Q4 (Jan-Mar)" },
  ];
  const HALVES = [
    { value: "1", label: "H1 (Apr-Sep)" }, { value: "2", label: "H2 (Oct-Mar)" },
  ];

  // Get current fiscal month (1=Apr, 2=May, ..., 12=Mar)
  const getCurrentFiscalMonth = () => {
    const month = new Date().getMonth(); // 0-indexed
    return month >= 3 ? month - 2 : month + 10;
  };

  const handleMonthQuickSelect = (monthValue) => {
    if (periodType === "monthly" && periodValue === monthValue) {
      // Deselect - go back to "all"
      setPeriodType("all");
      setPeriodValue("all");
    } else {
      setPeriodType("monthly");
      setPeriodValue(monthValue);
    }
  };

  const handleQuarterQuickSelect = (quarterValue) => {
    if (periodType === "quarterly" && periodValue === quarterValue) {
      setPeriodType("all");
      setPeriodValue("all");
    } else {
      setPeriodType("quarterly");
      setPeriodValue(quarterValue);
    }
  };

  const downloadExcel = () => {
    if (!analytics || analytics.restaurant_rankings.length === 0) {
      toast.error("No data to export");
      return;
    }
    const data = analytics.restaurant_rankings.map(rest => ({
      'Rank': rest.rank,
      'Restaurant': rest.restaurant_name,
      'Target': rest.total_target,
      'Actual': rest.total_actual,
      'Achievement %': rest.achievement_percentage.toFixed(1),
      'Variance': rest.variance,
    }));
    data.push({
      'Rank': '', 'Restaurant': 'TOTAL',
      'Target': analytics.overall.total_target,
      'Actual': analytics.overall.total_actual,
      'Achievement %': analytics.overall.achievement_percentage.toFixed(1),
      'Variance': analytics.overall.total_actual - analytics.overall.total_target,
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Restaurant Rankings");

    if (analytics.store_manager_rankings.length > 0) {
      const mgrData = analytics.store_manager_rankings.map(mgr => ({
        'Rank': mgr.rank,
        'Store Manager': mgr.store_manager_name,
        'Restaurants': mgr.restaurants.join(", "),
        'Target': mgr.total_target,
        'Actual': mgr.total_actual,
        'Achievement %': mgr.achievement_percentage.toFixed(1),
        'Variance': mgr.variance,
      }));
      const ws2 = XLSX.utils.json_to_sheet(mgrData);
      XLSX.utils.book_append_sheet(wb, ws2, "Manager Rankings");
    }

    if (analytics.period_breakdown.length > 0) {
      const periodData = analytics.period_breakdown.map(p => ({
        'Period': p.period_label,
        'Target': p.total_target,
        'Actual': p.total_actual,
        'Achievement %': p.achievement_percentage.toFixed(1),
      }));
      const ws3 = XLSX.utils.json_to_sheet(periodData);
      XLSX.utils.book_append_sheet(wb, ws3, "Period Breakdown");
    }

    XLSX.writeFile(wb, `Target_Analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel file downloaded!");
  };

  const downloadPDF = () => {
    if (!analytics || analytics.restaurant_rankings.length === 0) {
      toast.error("No data to export");
      return;
    }
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const fmtCurrency = (val) => `Rs. ${Number(val).toLocaleString('en-IN')}`;

    // Header band
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text("Target Analytics Report", 14, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const fyText = fiscalYear !== "all" ? `FY ${fiscalYear}-${(parseInt(fiscalYear) + 1).toString().slice(2)}` : "All Years";
    doc.text(`${fyText}  |  Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`, 14, 28);
    doc.text("N Square Cafe", pageWidth - 14, 18, { align: 'right' });

    // Summary cards
    doc.setTextColor(0, 0, 0);
    const startY = 45;
    const cardWidth = 63;
    const cardGap = 6;
    const cards = [
      { label: "Total Target", value: fmtCurrency(analytics.overall.total_target), color: [59, 130, 246] },
      { label: "Total Actual", value: fmtCurrency(analytics.overall.total_actual), color: [34, 197, 94] },
      { label: "Achievement", value: `${analytics.overall.achievement_percentage.toFixed(1)}%`, color: analytics.overall.achievement_percentage >= 80 ? [34, 197, 94] : [239, 68, 68] },
      { label: "On Track / Behind", value: `${analytics.overall.on_track_count} / ${analytics.overall.behind_count}`, color: [147, 51, 234] },
    ];

    cards.forEach((card, i) => {
      const x = 14 + i * (cardWidth + cardGap);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, startY, cardWidth, 22, 2, 2, 'F');
      doc.setDrawColor(card.color[0], card.color[1], card.color[2]);
      doc.setLineWidth(0.8);
      doc.line(x, startY, x, startY + 22);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.setFont(undefined, 'normal');
      doc.text(card.label, x + 5, startY + 8);
      doc.setFontSize(13);
      doc.setTextColor(card.color[0], card.color[1], card.color[2]);
      doc.setFont(undefined, 'bold');
      doc.text(card.value, x + 5, startY + 18);
    });

    // Restaurant rankings table
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text("Restaurant Leaderboard", 14, startY + 35);

    const tableData = analytics.restaurant_rankings.map(rest => [
      `#${rest.rank}`,
      rest.restaurant_name,
      fmtCurrency(rest.total_target),
      fmtCurrency(rest.total_actual),
      `${rest.achievement_percentage.toFixed(1)}%`,
      `${rest.variance >= 0 ? '+' : '-'} ${fmtCurrency(Math.abs(rest.variance))}`,
    ]);

    autoTable(doc, {
      startY: startY + 38,
      head: [['Rank', 'Restaurant', 'Target', 'Actual', 'Achievement', 'Variance']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 60 },
        2: { cellWidth: 45, halign: 'right' },
        3: { cellWidth: 45, halign: 'right' },
        4: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
        5: { cellWidth: 50, halign: 'right' },
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 4) {
          const val = parseFloat(data.cell.raw);
          if (val >= 100) data.cell.styles.textColor = [34, 197, 94];
          else if (val >= 80) data.cell.styles.textColor = [234, 179, 8];
          else data.cell.styles.textColor = [239, 68, 68];
        }
        if (data.section === 'body' && data.column.index === 5) {
          const text = data.cell.raw || '';
          if (text.startsWith('+')) data.cell.styles.textColor = [34, 197, 94];
          else data.cell.styles.textColor = [239, 68, 68];
        }
      }
    });

    // Store manager page
    if (analytics.store_manager_rankings.length > 0) {
      doc.addPage();
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text("Store Manager Performance", 14, 16);

      const mgrData = analytics.store_manager_rankings.map(mgr => [
        `#${mgr.rank}`,
        mgr.store_manager_name,
        mgr.restaurants.join(", "),
        fmtCurrency(mgr.total_target),
        fmtCurrency(mgr.total_actual),
        `${mgr.achievement_percentage.toFixed(1)}%`,
      ]);
      autoTable(doc, {
        startY: 33,
        head: [['Rank', 'Manager', 'Restaurants', 'Target', 'Actual', 'Achievement']],
        body: mgrData,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
          1: { cellWidth: 45 },
          2: { cellWidth: 70 },
          3: { cellWidth: 45, halign: 'right' },
          4: { cellWidth: 45, halign: 'right' },
          5: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 5) {
            const val = parseFloat(data.cell.raw);
            if (val >= 100) data.cell.styles.textColor = [34, 197, 94];
            else if (val >= 80) data.cell.styles.textColor = [234, 179, 8];
            else data.cell.styles.textColor = [239, 68, 68];
          }
        }
      });
    }

    // Period breakdown page
    if (analytics.period_breakdown.length > 0) {
      doc.addPage();
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text("Period Breakdown", 14, 16);

      const periodData = analytics.period_breakdown.map(p => [
        p.period_label,
        fmtCurrency(p.total_target),
        fmtCurrency(p.total_actual),
        `${p.achievement_percentage.toFixed(1)}%`,
      ]);
      autoTable(doc, {
        startY: 33,
        head: [['Period', 'Target', 'Actual', 'Achievement']],
        body: periodData,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 55, halign: 'right' },
          2: { cellWidth: 55, halign: 'right' },
          3: { cellWidth: 35, halign: 'center', fontStyle: 'bold' },
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 3) {
            const val = parseFloat(data.cell.raw);
            if (val >= 100) data.cell.styles.textColor = [34, 197, 94];
            else if (val >= 80) data.cell.styles.textColor = [234, 179, 8];
            else data.cell.styles.textColor = [239, 68, 68];
          }
        }
      });
    }

    // Footer on all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
      doc.text("N Square Cafe - Target Analytics", 14, pageHeight - 8);
    }

    doc.save(`Target_Analytics_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF file downloaded!");
  };

  const getPeriodValueOptions = () => {
    if (periodType === "monthly") return FISCAL_MONTHS;
    if (periodType === "quarterly") return QUARTERS;
    if (periodType === "half_yearly") return HALVES;
    return [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Target Analytics</h2>
          <p className="text-muted-foreground">
            Comprehensive performance dashboard for restaurant targets
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadExcel} variant="outline" size="sm" className="gap-2" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button onClick={downloadPDF} variant="outline" size="sm" className="gap-2" data-testid="export-pdf-btn">
            <FileText className="w-4 h-4" /> PDF
          </Button>
          <Button onClick={fetchAnalytics} disabled={loading} variant="outline" size="sm" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Fiscal Year */}
            <div className="space-y-2">
              <Label className="text-sm">Fiscal Year</Label>
              <Select value={fiscalYear} onValueChange={setFiscalYear}>
                <SelectTrigger data-testid="filter-fiscal-year">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {generateFiscalYears().map((fy) => (
                    <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Brand */}
            <div className="space-y-2">
              <Label className="text-sm">Brand</Label>
              <Select value={brandId} onValueChange={setBrandId}>
                <SelectTrigger data-testid="filter-brand">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Restaurant */}
            <div className="space-y-2">
              <Label className="text-sm">Restaurant</Label>
              <Select value={restaurantId} onValueChange={setRestaurantId}>
                <SelectTrigger data-testid="filter-restaurant">
                  <SelectValue placeholder="Select restaurant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants && restaurants.map((rest) => (
                    <SelectItem key={rest.id} value={rest.id}>{rest.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period Type */}
            <div className="space-y-2">
              <Label className="text-sm">Period Type</Label>
              <Select value={periodType} onValueChange={(val) => { setPeriodType(val); setPeriodValue("all"); }}>
                <SelectTrigger data-testid="filter-period-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Specific Period (shows when a period type is selected) */}
            {periodType !== "all" && periodType !== "yearly" && (
              <div className="space-y-2">
                <Label className="text-sm">
                  {periodType === "monthly" ? "Month" : periodType === "quarterly" ? "Quarter" : "Half"}
                </Label>
                <Select value={periodValue} onValueChange={setPeriodValue}>
                  <SelectTrigger data-testid="filter-period-value">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {getPeriodValueOptions().map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Store Manager */}
            <div className="space-y-2">
              <Label className="text-sm">Store Manager</Label>
              <Select value={storeManagerId} onValueChange={setStoreManagerId}>
                <SelectTrigger data-testid="filter-store-manager">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {storeManagers.map((mgr) => (
                    <SelectItem key={mgr.id} value={mgr.id}>{mgr.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick Month & Quarter Select */}
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Month:</span>
              {FISCAL_MONTHS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => handleMonthQuickSelect(m.value)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                    periodType === "monthly" && periodValue === m.value
                      ? "bg-blue-600 text-white border-blue-600"
                      : parseInt(m.value) === getCurrentFiscalMonth()
                        ? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  data-testid={`month-pill-${m.value}`}
                >
                  {m.short}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Quarter:</span>
              {QUARTERS.map((q) => (
                <button
                  key={q.value}
                  onClick={() => handleQuarterQuickSelect(q.value)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    periodType === "quarterly" && periodValue === q.value
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  data-testid={`quarter-pill-${q.value}`}
                >
                  {q.label}
                </button>
              ))}
              {(periodType === "monthly" || periodType === "quarterly") && periodValue !== "all" && (
                <button
                  onClick={() => { setPeriodType("all"); setPeriodValue("all"); }}
                  className="px-3 py-1 text-xs font-medium rounded-full border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                  data-testid="clear-period-filter"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overall Stats */}
      {analytics && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Overall Achievement Ring */}
            <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="pt-6 flex flex-col items-center">
                <ProgressRing 
                  percentage={analytics.overall.achievement_percentage} 
                  size={140}
                  strokeWidth={12}
                />
                <p className="mt-4 text-sm text-gray-600 font-medium">Overall Achievement</p>
                <p className="text-xs text-gray-500">{analytics.overall.target_count} targets tracked</p>
              </CardContent>
            </Card>

            {/* Total Target */}
            <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Total Target</span>
                </div>
                <p className="text-3xl font-bold text-blue-600">
                  <CurrencyDisplay amount={analytics.overall.total_target} />
                </p>
              </CardContent>
            </Card>

            {/* Total Actual */}
            <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-100">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Total Actual</span>
                </div>
                <p className="text-3xl font-bold text-green-600">
                  <CurrencyDisplay amount={analytics.overall.total_actual} />
                </p>
              </CardContent>
            </Card>

            {/* Status Breakdown */}
            <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Trophy className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-600">Performance Status</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{analytics.overall.on_track_count}</p>
                    <p className="text-xs text-gray-500">On Track</p>
                  </div>
                  <div className="h-8 w-px bg-gray-200" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{analytics.overall.behind_count}</p>
                    <p className="text-xs text-gray-500">Behind</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Restaurant Leaderboard */}
          <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Restaurant Leaderboard
              </CardTitle>
              <CardDescription>
                Performance ranking by restaurant
              </CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.restaurant_rankings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No restaurant data available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Rank</TableHead>
                      <TableHead>Restaurant</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Achievement</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.restaurant_rankings.map((rest) => (
                      <TableRow key={rest.restaurant_id} data-testid={`restaurant-row-${rest.restaurant_id}`}>
                        <TableCell>
                          <RankBadge rank={rest.rank} />
                        </TableCell>
                        <TableCell className="font-medium">{rest.restaurant_name}</TableCell>
                        <TableCell className="text-right">
                          <CurrencyDisplay amount={rest.total_target} />
                        </TableCell>
                        <TableCell className="text-right">
                          <CurrencyDisplay amount={rest.total_actual} />
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${
                            rest.achievement_percentage >= 100 ? 'text-green-600' :
                            rest.achievement_percentage >= 80 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {rest.achievement_percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <VarianceIndicator variance={rest.variance} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Store Manager Performance */}
          {analytics.store_manager_rankings.length > 0 && (
            <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Store Manager Performance
                </CardTitle>
                <CardDescription>
                  Performance ranking by store manager
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Rank</TableHead>
                      <TableHead>Store Manager</TableHead>
                      <TableHead>Restaurants</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Achievement</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.store_manager_rankings.map((mgr) => (
                      <TableRow key={mgr.store_manager_id} data-testid={`manager-row-${mgr.store_manager_id}`}>
                        <TableCell>
                          <RankBadge rank={mgr.rank} />
                        </TableCell>
                        <TableCell className="font-medium">{mgr.store_manager_name}</TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600">
                            {mgr.restaurants.join(", ")}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <CurrencyDisplay amount={mgr.total_target} />
                        </TableCell>
                        <TableCell className="text-right">
                          <CurrencyDisplay amount={mgr.total_actual} />
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${
                            mgr.achievement_percentage >= 100 ? 'text-green-600' :
                            mgr.achievement_percentage >= 80 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {mgr.achievement_percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <VarianceIndicator variance={mgr.variance} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Period Breakdown */}
          {analytics.period_breakdown.length > 0 && (
            <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Period Breakdown
                </CardTitle>
                <CardDescription>
                  Performance by period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Achievement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.period_breakdown.map((period, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{period.period_label}</TableCell>
                        <TableCell className="text-right">
                          <CurrencyDisplay amount={period.total_target} />
                        </TableCell>
                        <TableCell className="text-right">
                          <CurrencyDisplay amount={period.total_actual} />
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${
                            period.achievement_percentage >= 100 ? 'text-green-600' :
                            period.achievement_percentage >= 80 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {period.achievement_percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Loading State */}
      {loading && !analytics && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
};

export default TargetAnalytics;
